import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, useFormikContext } from 'formik';
import { differenceInYears } from 'date-fns';
import { Grid } from '@material-ui/core';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import flowRight from 'lodash-es/flowRight';

import {
  lookupsJson,
  courseDiscountTypeByStates,
  additionalDriverDiscountsTypesByStates,
  stateSpecificToolTips,
  hideHighestEducationStates,
  hideGenderStates,
  sR22States,
  noCreditStates,
  lapsedLicenseMonthsStates,
  seniorAge,
  inexperiencedYearsLimit
} from '@ourbranch/lookups';

import { useStore } from 'core/store/store.mobx';
import useSession from 'core/hooks/use-session';
import { Label } from 'core/components/label';
import { FormField } from 'core/components/form';
import withDatePicker from 'core/components/with-date-picker';
import CollapsibleCard from 'core/components/collapsible-card';
import excluded from 'core/assets/svg/ic-excluded.svg';
import { Tooltip } from 'core/components/tooltip';
import { useToast } from 'core/components/toast';
import { useSwapAppCoApp } from 'common/components/people/hooks';
import { tooltipHoverTexts } from 'core/helpers/constants';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import ClearUDRsModal from './clear-udrs-modal/clear-udrs-modal';
import CommunityDriveCard from './community-drive-card/policy-community-drive-card';
import {
  canPersonBeFullTimeStudent,
  canPersonReceiveDrivingCourseDiscount,
  canReceiveCommunityDriveDiscount
} from './helpers';
import useStyles from './person.styles';

const drivingCourseLabel = {
  AVOIDANCE: 'Has this driver taken an Accident Avoidance Course?',
  SENIOR: 'Has this driver taken an Accident Prevention Course?',
  DEFENSIVE: 'Has this driver taken a Defensive Driving Course?',
  PREVENTION: 'Has this driver taken an Accident Prevention Course?',
  MILITARY_DEFENSIVE:
    'Has this driver taken a voluntary accident prevention course provided by the Armed Forces in the last 5 years?',
  NATIONAL_GUARD: 'Has this driver taken a defensive driving course?',
  IS_ACTIVE_DUTY: 'Is this driver an active duty member of the US Armed Forces?',
  IS_ACTIVE_DUTY_NG: 'Is this driver an active duty member of the National Guard?',
  INEXPERIENCED: 'Has this driver taken a Driver Training Course in the last 3 years?'
};

const Driver = ({
  index,
  fieldName,
  item,
  initialFold,
  openSignalOnError,
  disabled,
  hasAuto,
  onRecheckDrivingRecord,
  maritalStatusOptions,
  state,
  actions,
  allowLicensedActions,
  fromPolicy
}) => {
  const classes = useStyles();
  const { canClearUDRs, viewOnly, canEditDriversName } = useSession();
  const { offer: offerStore, account: accountStore } = useStore();
  const { inPolicyRoute } = useRouteHelper();

  const toast = useToast();

  const { setFieldValue: formikSetFieldValue, values } = useFormikContext();

  const { drivers } = values;
  const { isSwapping } = useSwapAppCoApp({ formikPath: 'drivers', allowLicensedActions });

  const [openClearUDRViolationModal, setOpenClearUDRViolationModal] = useState(false);
  const clearUDRViolationsIsChecked = values.drivers[index].clearUDRViolation;

  const today = new Date();
  const birthDate = new Date(item.dateOfBirth);
  const age = differenceInYears(today, birthDate);
  const drivingCourseDiscountType = courseDiscountTypeByStates[state];

  const rateControlDate = inPolicyRoute
    ? accountStore?.policies?.policy?.policy?.rateControlDate
    : offerStore?.offer?.quote?.global?.rateControlDate;

  const canBeFullTimeStudent = canPersonBeFullTimeStudent({
    driverOrPerson: item,
    driversOrPeople: drivers,
    rateControlDate
  });
  const driverExcluded = item.excludeDriver;
  const hasUDR = item?.autoViolations?.UDR >= 1;

  useEffect(() => {
    if (!canBeFullTimeStudent || !item?.fullTimeStudent) {
      formikSetFieldValue(`${fieldName}.fullTimeStudent`, false);
      formikSetFieldValue(`${fieldName}.goodStudent`, false);
      formikSetFieldValue(`${fieldName}.schoolName`, null);
      formikSetFieldValue(`${fieldName}.schoolLocation`, null);
    }
  }, [fieldName, item.fullTimeStudent, canBeFullTimeStudent, formikSetFieldValue]);

  const handleStudentChange = (_, val) => {
    if (val) {
      formikSetFieldValue(`${fieldName}.fullTimeStudent`, true);
      formikSetFieldValue(`${fieldName}.schoolName`, null);
      formikSetFieldValue(`${fieldName}.goodStudent`, false);
      formikSetFieldValue(`${fieldName}.schoolLocation`, {});
    } else {
      // Cascade-clear on uncheck. Leaving school fields behind persists a driver with
      // fullTimeStudent=false but schoolName/schoolLocation set, which the cleanup effect
      // above re-clears on every mount — keeping the form permanently dirty and the offer
      // footer locked on "Update offer" (https://github.com/gobranch/branch/issues/31834).
      formikSetFieldValue(`${fieldName}.fullTimeStudent`, false);
      formikSetFieldValue(`${fieldName}.goodStudent`, false);
      formikSetFieldValue(`${fieldName}.schoolName`, null);
      formikSetFieldValue(`${fieldName}.schoolLocation`, null);
    }
  };

  const { canReceiveDrivingCourseDiscount, canReceiveInexperiencedDrivingCourseDiscount } =
    canPersonReceiveDrivingCourseDiscount({ driverOrPerson: item, state });

  const eligibleForDrivingCourseDiscount =
    canReceiveDrivingCourseDiscount ||
    canReceiveInexperiencedDrivingCourseDiscount ||
    (!canReceiveDrivingCourseDiscount &&
      !canReceiveInexperiencedDrivingCourseDiscount &&
      additionalDriverDiscountsTypesByStates[state]?.condition);

  useEffect(() => {
    if (!item?.courseDiscountConditionCheck && !canReceiveDrivingCourseDiscount) {
      formikSetFieldValue(`${fieldName}.takenCourse`, item?.courseDiscountConditionCheck);
    }
  }, [fieldName, formikSetFieldValue, item.courseDiscountConditionCheck, canReceiveDrivingCourseDiscount]);

  if (item.isCoApplicant && item.excludeDriver) {
    // this would happen if the person is the Co-App on the Home Only offer but excluded on the Auto offer.
    // If they switch to home and auto, we want honor the excluded variable over co-app
    formikSetFieldValue(`${fieldName}.isCoApplicant`, false);
    toast.notify({
      type: 'success',
      message: `${item.firstName} ${item.lastName} has been updated to not be a co-applicant on this offer type since they are an excluded driver. Please update the offer to continue with this offer type.`
    });
  }

  useEffect(() => {
    if (state === 'MA' && !values.drivers[index].userAssignedCar) {
      const { ageFirstLicensed, assignedCar, id } = values.drivers[index];
      // we assign a default car in case some of the users are removed without updating the policy
      let carIx = -1;
      if (assignedCar) {
        carIx = values.cars.findIndex((car) => car.VIN === assignedCar);
      }
      if (age >= seniorAge[state]) {
        formikSetFieldValue(`${fieldName}.userAssignedCar`, assignedCar || values.cars[0]?.VIN);
        formikSetFieldValue(`cars.${carIx < 0 ? 0 : carIx}.userAssignedDriver`, id);
      }
      if (age - ageFirstLicensed < inexperiencedYearsLimit[state]) {
        formikSetFieldValue(`${fieldName}.userAssignedCar`, assignedCar || 'NONE');
        if (carIx >= 0) {
          formikSetFieldValue(`cars.${carIx}.userAssignedDriver`, id);
        }
      }
    }
  }, [
    fieldName,
    item.ageFirstLicensed,
    age,
    formikSetFieldValue,
    state,
    values.cars,
    values.cars?.length,
    index,
    values.drivers
  ]);

  useEffect(() => {
    if (
      !item?.courseDiscountConditionCheck &&
      !canReceiveDrivingCourseDiscount &&
      !canReceiveInexperiencedDrivingCourseDiscount
    ) {
      formikSetFieldValue(`${fieldName}.takenCourse`, item?.courseDiscountConditionCheck);
    }
  }, [
    fieldName,
    formikSetFieldValue,
    item.courseDiscountConditionCheck,
    canReceiveDrivingCourseDiscount,
    canReceiveInexperiencedDrivingCourseDiscount
  ]);

  const communityDriveOffered = canReceiveCommunityDriveDiscount({ state, isBix: values.isBix });

  const isFarm = offerStore.isFarmOffer || (inPolicyRoute && accountStore?.policies?.policy?.isFarmOffer);

  return (
    <>
      {openClearUDRViolationModal && (
        <ClearUDRsModal
          setOpenClearUDRViolationModal={setOpenClearUDRViolationModal}
          fieldName={fieldName}
          item={item}
        />
      )}
      <CollapsibleCard
        content={
          <>
            {driverExcluded && <img src={excluded} alt="excluded" />}
            <Label
              type="infoSubtitle"
              className={classNames({ [classes.driverExcludedLabel]: driverExcluded })}
            >{`${item.firstName} ${item.lastName}`}</Label>
            {driverExcluded && (
              <Label type="infoCardTitle" className={classes.driverExcludedReason}>
                Excluded{' '}
                <Tooltip
                  text={
                    item.exclusionReason ||
                    'This exclusion was either automatically added due to the policy’s violations or manually added by an agent'
                  }
                  placement="bottom"
                />
              </Label>
            )}
            {item.isPrimary && <Label type="infoCardTitle">Applicant</Label>}
          </>
        }
        collapsibleCardNotification={hasUDR ? 'This driver has an unverifiable driving record.' : null}
        contentRight={actions}
        initialOpen={
          initialFold ||
          item.initialOpen ||
          (canBeFullTimeStudent && offerStore?.uiHelpers?.eligibleStudentDriversCardsOpen)
        }
        openSignal={openSignalOnError}
        forceOpen={
          (canBeFullTimeStudent && offerStore?.uiHelpers?.eligibleStudentDriversCardsOpen) ||
          (offerStore?.uiHelpers?.eligibleDrivingCourseDriversCardsOpen && eligibleForDrivingCourseDiscount) ||
          (offerStore?.uiHelpers?.eligibleCommunityDriveDriversCardsOpen && communityDriveOffered)
        }
        className={classNames({ [classes.driverExcluded]: driverExcluded })}
      >
        <div className={classNames(classes.container, classes.driverContainer)}>
          <Form key={`driver-form-${disabled || item.excludeDriver}`} disabled={disabled || item.excludeDriver}>
            <Grid container alignItems="flex-start" spacing={4}>
              {inPolicyRoute && !canEditDriversName ? (
                <>
                  <FormField
                    name={`${fieldName}.firstName`}
                    type="value"
                    value={item.firstName || 'N/A'}
                    label="First Name"
                    mode="light"
                    xs={3}
                    fast={false}
                  />
                  <FormField
                    name={`${fieldName}.middleName`}
                    type="value"
                    label="Middle Name"
                    value={item.middleName || 'N/A'}
                    mode="light"
                    xs={3}
                    fast={false}
                  />
                  <FormField
                    name={`${fieldName}.lastName`}
                    type="value"
                    value={item.lastName || 'N/A'}
                    label="Last Name"
                    mode="light"
                    xs={3}
                    fast={false}
                  />
                  <FormField
                    name={`${fieldName}.suffix`}
                    type="value"
                    value={item.suffix || 'N/A'}
                    label="Suffix"
                    mode="light"
                    xs={2}
                    fast={false}
                  />
                </>
              ) : (
                <>
                  <FormField
                    id={`${fieldName}.firstName`}
                    name={`${fieldName}.firstName`}
                    type="string"
                    label="First Name"
                    mode="light"
                    xs={3}
                    disabled={isSwapping || driverExcluded} // can't change name in same update as swapping app / coapp
                    fast={false}
                  />
                  <FormField
                    id={`${fieldName}.middleName`}
                    name={`${fieldName}.middleName`}
                    type="string"
                    label="Middle Name"
                    mode="light"
                    xs={3}
                    disabled={isSwapping || driverExcluded} // can't change name in same update as swapping app / coapp
                    fast={false}
                  />
                  <FormField
                    id={`${fieldName}.lastName`}
                    name={`${fieldName}.lastName`}
                    type="string"
                    label="Last Name"
                    mode="light"
                    xs={3}
                    disabled={isSwapping || driverExcluded} // can't change name in same update as swapping app / coapp
                    fast={false}
                  />
                  <FormField
                    id={`${fieldName}.suffix`}
                    name={`${fieldName}.suffix`}
                    type="string"
                    disabled={driverExcluded}
                    label="Suffix"
                    mode="light"
                    xs={3}
                  />
                </>
              )}
              <FormField
                id={`${fieldName}.dateOfBirth`}
                name={`${fieldName}.dateOfBirth`}
                type="date"
                label="Date of Birth"
                disabled={driverExcluded}
                mode="light"
                xs={!hideGenderStates.includes(state) || hideHighestEducationStates.includes(state) ? 4 : 6}
                pii
              />
              {!hideGenderStates.includes(state) && (
                <FormField
                  id={`${fieldName}.gender`}
                  name={`${fieldName}.gender`}
                  type="select"
                  label="Gender"
                  mode="light"
                  options={lookupsJson.gender}
                  xs={4}
                  disabled={driverExcluded}
                  permissions={{ isLicensedAction: false }}
                />
              )}
              <FormField
                id={`${fieldName}.maritalStatus`}
                name={`${fieldName}.maritalStatus`}
                type="select"
                label="Marital Status"
                mode="light"
                options={maritalStatusOptions || lookupsJson.maritalStatus}
                xs={!hideGenderStates.includes(state) || hideHighestEducationStates.includes(state) ? 4 : 6}
                disabled={driverExcluded}
                permissions={{ isLicensedAction: false }}
              />
              {!hideHighestEducationStates.includes(state) && (
                <FormField
                  id={`${fieldName}.highestEducation`}
                  name={`${fieldName}.highestEducation`}
                  type="select"
                  label="Highest Education Achieved"
                  mode="light"
                  options={lookupsJson.highestEducation}
                  xs={6}
                  disabled={driverExcluded}
                  permissions={{ isLicensedAction: false }}
                />
              )}
              <FormField
                id={`${fieldName}.addDate`}
                name={`${fieldName}.addDate`}
                type="date"
                label="Covered Since"
                mode="light"
                xs={!hideHighestEducationStates.includes(state) ? 6 : 4}
                disabled={driverExcluded}
              />
              {!isFarm && (
                <>
                  <FormField
                    id={`${fieldName}.driversLicenseState`}
                    name={`${fieldName}.driversLicenseState`}
                    type="state"
                    label="License State"
                    mode="light"
                    disabled={driverExcluded}
                    xs={4}
                  />
                  <FormField
                    id={`${fieldName}.driversLicenseNumber`}
                    name={`${fieldName}.driversLicenseNumber`}
                    type="string"
                    label="License Number"
                    disabled={driverExcluded}
                    mode="light"
                    xs={4}
                    pii
                  />
                  <FormField
                    id={`${fieldName}.ageFirstLicensed`}
                    name={`${fieldName}.ageFirstLicensed`}
                    type="numeric"
                    label="First Licensed at"
                    mode="light"
                    xs={4}
                    disabled={driverExcluded}
                  />
                </>
              )}

              {canBeFullTimeStudent && (
                <FormField
                  id={`${fieldName}.fullTimeStudent`}
                  name={`${fieldName}.fullTimeStudent`}
                  type="switch"
                  label="Full Time Student"
                  disabled={driverExcluded}
                  mode="light"
                  xs={12}
                  onChange={handleStudentChange}
                />
              )}
              {canBeFullTimeStudent && item.fullTimeStudent && (
                <>
                  <Grid container item alignItems="flex-end" spacing={4}>
                    <FormField
                      id={`${fieldName}.schoolName`}
                      name={`${fieldName}.schoolName`}
                      type="string"
                      label="School Name"
                      disabled={driverExcluded}
                      mode="light"
                      xs={8}
                    />
                    <FormField
                      id={`${fieldName}.goodStudent`}
                      name={`${fieldName}.goodStudent`}
                      type="checkbox"
                      label="Good Student"
                      mode="light"
                      disabled={driverExcluded}
                      xs={4}
                    />
                  </Grid>
                  <FormField
                    id={`${fieldName}.schoolLocation.address`}
                    name={`${fieldName}.schoolLocation.address`}
                    type="string"
                    label="School Address"
                    disabled={driverExcluded}
                    mode="light"
                    xs={8}
                  />
                  <FormField
                    id={`${fieldName}.schoolLocation.address2`}
                    name={`${fieldName}.schoolLocation.address2`}
                    type="string"
                    label="Address 2"
                    disabled={driverExcluded}
                    mode="light"
                    xs={4}
                  />
                  <FormField
                    id={`${fieldName}.schoolLocation.city`}
                    name={`${fieldName}.schoolLocation.city`}
                    type="string"
                    label="City"
                    disabled={driverExcluded}
                    mode="light"
                    xs={4}
                  />
                  <FormField
                    id={`${fieldName}.schoolLocation.state`}
                    name={`${fieldName}.schoolLocation.state`}
                    type="state"
                    label="State"
                    mode="light"
                    disabled={driverExcluded}
                    xs={4}
                  />
                  <FormField
                    id={`${fieldName}.schoolLocation.zip`}
                    name={`${fieldName}.schoolLocation.zip`}
                    type="string"
                    label="Zip Code"
                    disabled={driverExcluded}
                    mode="light"
                    xs={4}
                  />
                </>
              )}
              {hasUDR && (
                <Grid item container spacing={5}>
                  {onRecheckDrivingRecord && (
                    <Grid item xs={12}>
                      <FormField
                        name={`${fieldName}.recheckDrivingRecord`}
                        id={`${fieldName}.recheckDrivingRecord`}
                        type="checkbox"
                        label="Re-run MVR reports to check this driver's driving record"
                        mode="light"
                        onChange={onRecheckDrivingRecord}
                        disabled={clearUDRViolationsIsChecked || viewOnly}
                        tooltipText="Checking this box will rerun MVR (motor vehicle records) reports when you update this offer. This can be helpful when drivers have unverifiable driving records."
                        tooltipLabel="More info"
                      />
                    </Grid>
                  )}
                  {canClearUDRs && (
                    <Grid item xs={12}>
                      <FormField
                        id={`${fieldName}.clearUDRViolation`}
                        name={`${fieldName}.clearUDRViolation`}
                        type="checkbox"
                        label="Clear UDR Violation"
                        mode="light"
                        xs={6}
                        onClick={(e) => {
                          setOpenClearUDRViolationModal(e.target.checked);
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              )}
              {!canReceiveDrivingCourseDiscount &&
                !canReceiveInexperiencedDrivingCourseDiscount &&
                additionalDriverDiscountsTypesByStates[state]?.condition && (
                  // if not elligible for the standard discount, show field for any extra conditions to meet before qualifying for additional discount
                  <FormField
                    id={`${fieldName}.courseDiscountConditionCheck`}
                    name={`${fieldName}.courseDiscountConditionCheck`}
                    type="checkbox"
                    label={drivingCourseLabel[additionalDriverDiscountsTypesByStates[state]?.condition]}
                    mode="light"
                    xs={12}
                    disabled={driverExcluded}
                  />
                )}
              {canReceiveDrivingCourseDiscount || canReceiveInexperiencedDrivingCourseDiscount ? (
                <Grid item container display="flex-flow">
                  <Grid item xs={8}>
                    <FormField
                      id={`${fieldName}.takenCourse`}
                      name={`${fieldName}.takenCourse`}
                      type="checkbox"
                      className={classes.takenCourseContainer}
                      disabled={driverExcluded}
                      label={drivingCourseLabel[drivingCourseDiscountType]}
                      mode="light"
                      tooltipText={
                        stateSpecificToolTips[state] ? tooltipHoverTexts[stateSpecificToolTips[state]] : null
                      }
                      tooltipLabel={stateSpecificToolTips[state] ? 'More Info' : null}
                    />
                  </Grid>
                </Grid>
              ) : (
                item?.courseDiscountConditionCheck &&
                drivingCourseLabel[additionalDriverDiscountsTypesByStates[state]?.discountType] && (
                  <FormField
                    id={`${fieldName}.takenCourse`}
                    name={`${fieldName}.takenCourse`}
                    disabled={driverExcluded}
                    type="checkbox"
                    label={drivingCourseLabel[additionalDriverDiscountsTypesByStates[state]?.discountType]}
                    mode="light"
                    xs={12}
                  />
                )
              )}
              {lapsedLicenseMonthsStates[state] && hasAuto && (
                <FormField
                  id={`${fieldName}.lapsedLicense`}
                  name={`${fieldName}.lapsedLicense`}
                  disabled={driverExcluded}
                  type="checkbox"
                  label={`Has this driver had a lapsed driver's license in the last ${lapsedLicenseMonthsStates[state]} months?`}
                  mode="light"
                  xs={12}
                />
              )}
              {inPolicyRoute && (
                <CommunityDriveCard
                  fieldName={fieldName}
                  index={index}
                  fromPolicy={fromPolicy}
                  driver={item}
                  state={state}
                />
              )}
              {hasAuto && sR22States[state] && (
                <Grid item xs={4}>
                  <FormField
                    id={`${fieldName}.needSR22`}
                    name={`${fieldName}.needSR22`}
                    type="checkbox"
                    label="Needs SR-22"
                    mode="light"
                    tooltipText="For any driver with a Financial Responsibility filing made against them, this box must be checked and an SR-22 form must be completed to be held on file with the state DMV. A template can be found in Guru"
                    tooltipLabel="More Info"
                    disabled={driverExcluded}
                  />
                </Grid>
              )}
            </Grid>
          </Form>
        </div>
        {(item.isPrimary || item.isCoApplicant) && item.insuranceScore && (
          <Grid container alignItems="center" justifyContent="flex-end" className={classes.cardFooter}>
            <Grid container item xs={3} direction="column" alignItems="flex-end">
              <Label type="infoLabel">Insurance Score</Label>
              <Label type="infoValue">
                {noCreditStates[state]
                  ? 'N/A'
                  : Number(item.insuranceScore?.autoTotal?.toFixed(1) || item.insuranceScore?.total?.toFixed(1))}
              </Label>
            </Grid>
          </Grid>
        )}
      </CollapsibleCard>
    </>
  );
};

Driver.propTypes = {
  index: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  item: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    middleName: PropTypes.string,
    lastName: PropTypes.string.isRequired,
    dateOfBirth: PropTypes.string.isRequired,
    addDate: PropTypes.string,
    ageFirstLicensed: PropTypes.number,
    courseDiscountConditionCheck: PropTypes.bool,
    driversLicenseNumber: PropTypes.string,
    driversLicenseState: PropTypes.string,
    exclusionReason: PropTypes.string,
    excludeDriver: PropTypes.bool,
    fullTimeStudent: PropTypes.bool,
    gender: PropTypes.string,
    goodStudent: PropTypes.bool,
    highestEducation: PropTypes.string,
    isCoApplicant: PropTypes.bool,
    isPrimary: PropTypes.bool,
    initialOpen: PropTypes.bool,
    maritalStatus: PropTypes.string,
    schoolLocation: PropTypes.object,
    schoolName: PropTypes.string,
    suffix: PropTypes.string,
    insuranceScore: PropTypes.shape({
      autoTotal: PropTypes.number,
      total: PropTypes.number
    }),
    autoViolations: PropTypes.shape({
      UDR: PropTypes.number
    })
  }).isRequired,
  fieldId: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired,
  removeFromList: PropTypes.func.isRequired,
  initialFold: PropTypes.bool.isRequired,
  openSignalOnError: PropTypes.number,
  allowLicensedActions: PropTypes.bool.isRequired,
  fromPolicy: PropTypes.bool,
  hasAuto: PropTypes.bool,
  showResetCommunityDrive: PropTypes.bool,
  onRecheckDrivingRecord: PropTypes.func.isRequired,
  maritalStatusOptions: PropTypes.array.isRequired,
  state: PropTypes.string.isRequired,
  actions: PropTypes.array.isRequired
};

Driver.defaultProps = {
  disabled: false,
  openSignalOnError: 0,
  fromPolicy: false,
  hasAuto: false,
  showResetCommunityDrive: false
};

export default flowRight(observer, withDatePicker)(Driver);
